import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Loader2, Lock } from "lucide-react";
import { api } from "../api";
import { useApp } from "../store";

const inputCls =
  "w-full bg-white/[0.05] border border-white/15 focus:border-[var(--accent)] focus:outline-none px-4 py-3.5 text-sm text-white placeholder:text-white/35 transition-colors duration-300";

export default function AdminLogin() {
  const { t } = useApp();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      localStorage.setItem("pgp_token", data.token);
      navigate("/admin/dashboard");
    } catch (err) {
      const detail = err.response?.data?.detail;
      setError(typeof detail === "string" ? detail : t("login_error"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0B132B] grain relative flex items-center justify-center px-5" data-testid="admin-login-page">
      <motion.div
        initial={{ opacity: 0, y: 32 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
        className="relative z-10 w-full max-w-md"
      >
        <form
          data-testid="admin-login-form"
          onSubmit={submit}
          className="bg-[#172A45]/70 backdrop-blur-xl border border-white/10 p-8 sm:p-10"
        >
          <span className="w-12 h-12 grid place-items-center bg-[var(--accent)] text-[#0B132B]">
            <Lock size={20} />
          </span>
          <h1 className="mt-6 font-display font-semibold text-3xl">{t("login_title")}</h1>
          <p className="mt-1 text-sm text-white/50">{t("login_sub")}</p>

          <div className="mt-8 flex flex-col gap-5">
            <div>
              <label htmlFor="admin-email" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("login_email")}
              </label>
              <input
                id="admin-email"
                data-testid="admin-login-email-input"
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className={inputCls}
                autoComplete="username"
              />
            </div>
            <div>
              <label htmlFor="admin-password" className="block font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/50 mb-2">
                {t("login_password")}
              </label>
              <input
                id="admin-password"
                data-testid="admin-login-password-input"
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className={inputCls}
                autoComplete="current-password"
              />
            </div>
            {error && (
              <p data-testid="admin-login-error" className="text-sm text-[var(--accent)]">
                {error}
              </p>
            )}
            <button
              data-testid="admin-login-submit-button"
              type="submit"
              disabled={loading}
              className="inline-flex items-center justify-center gap-2 bg-[var(--accent)] hover:bg-[var(--accent-soft)] disabled:opacity-60 text-white font-semibold px-7 py-4 rounded-full transition-colors duration-300"
            >
              {loading && <Loader2 size={17} className="animate-spin" />}
              {loading ? t("login_loading") : t("login_button")}
            </button>
          </div>
        </form>

        <Link
          data-testid="admin-login-back-link"
          to="/"
          className="mt-6 inline-flex items-center gap-2 font-mono2 text-[11px] uppercase tracking-[0.25em] text-white/40 hover:text-[var(--accent)] transition-colors duration-300"
        >
          <ArrowLeft size={13} />
          {t("login_back")}
        </Link>
      </motion.div>
    </div>
  );
}
